import { makeObservable, observable, action, computed } from "mobx";
import FilterSortStore from "../FilterSort/FilterSortStore";
import {FolderStore} from "./FolderStore";
import {DocumentStore} from "./DocumentStore";

class BaseDriveStore {
  folderList = [];
  documentList = [];
  filterText = "";
  
  isLoading = false;
  error = null;

  constructor({ foldersStore, documentsStore, agentsStore, selectionStore }) {
    this.foldersStore = foldersStore;
    this.documentsStore = documentsStore;
    this.agentsStore = agentsStore;
    this.filterSortStore = new FilterSortStore();
    this.selectionStore = selectionStore;

    makeObservable(this, {
      // Lists
      folderList: observable,
      documentList: observable,
      filterText: observable,

      // Loading state
      isLoading: observable,
      error: observable,

      // Actions
      setFolderList: action,
      setDocumentList: action,
      setFilterText: action,

      // Computed
      sortedFolders: computed,
      sortedDocuments: computed,
      filteredFolders: computed,
      filteredDocuments: computed,
    });
  }

  // List setters
  setFolderList = (folders) => {
      this.folderList = folders.map((data) => {
          const updatedAt = data.updated_at || data.updatedAt;
          const lastUpdated = data.lastUpdated || updatedAt || '';
          
          return new FolderStore({
              id: data.id,
              title: data.title,
              lastUpdated: lastUpdated,
              lastUpdatedTooltip: data.lastUpdatedTooltip || updatedAt || '',
              folderCount: data.folderCount,
              folder_uuid: data.folder_uuid || data.folderUuid,
              name: data.name,
              internal_name: data.internal_name,
              path: data.path,
              is_active: data.is_active,
              created_at: data.created_at || data.createdAt,
              updated_at: updatedAt,
              parent_folder_uuid: data.parent_folder_uuid || data.parentFolderUuid,
              sharedCountWith: data.sharedCountWith || data.shared_count_with || 0
          });
      });
  };

  setDocumentList = (documents) => {
      this.documentList = documents.map((data) => new DocumentStore({
          id: data.id,
          title: data.title,
          lastUpdated: data.lastUpdated,
          lastUpdatedTooltip: data.lastUpdatedTooltip,
          folderCount: data.folderCount,
          owner: data.owner,
          date: data.date,
          status: data.status,
          tags: data.tags,
          //data from contract - handle both camelCase (backend) and snake_case (frontend)
          document_uuid: data.document_uuid || data.documentUuid,
          vault_uuid: data.vault_uuid || data.vaultUuid,
          name: data.name,
          created_at: data.created_at || data.createdAt,
          updated_at: data.updated_at || data.updatedAt,
          description: data.description,
          active: data.active,
          version: data.version,
          scope: data.scope,
          summary: data.summary,
          path: data.path,
          owner_uuid: data.owner_uuid || data.ownerUuid,
          owner_full_name: data.owner_full_name || data.ownerFullName
      }));
  };

  setFilterText = (text) => {
    this.filterText = text;
  };

  // Sort proxy
  setSortBy = (field) => {
    this.filterSortStore.setSortBy(field);
  };

  get sortBy() {
    return this.filterSortStore.sortBy;
  }

  get sortDirection() {
    return this.filterSortStore.sortDirection;
  }

  // Computed
  get sortedFolders() {
    return this.filterSortStore.sortItems(this.folderList);
  }

  get sortedDocuments() {
    return this.filterSortStore.sortItems(this.documentList);
  }

  get filteredFolders() {
    let filtered = this.folderList;

    if (this.filterText) {
      filtered = filtered.filter(folder => {
        const title = folder.title || folder.name || '';
        return title.toLowerCase().includes(this.filterText.toLowerCase());
      });
    }

    return this.filterSortStore.sortItems(filtered);
  }

  get filteredDocuments() {
    let filtered = this.documentList;

    if (this.filterText) {
      filtered = filtered.filter(document => {
        const title = document.title || document.name || '';
        return title.toLowerCase().includes(this.filterText.toLowerCase());
      });
    }

    return this.filterSortStore.sortItems(filtered);
  }
}

export default BaseDriveStore;